import type { QueryData as BaseQueryData } from "@supabase/supabase-js";
import type { PostgrestBuilder } from "@supabase/postgrest-js";
import type { Client } from "./database.js";

/* Generics for handling queries */

type Query<R> = (api: Client, ...args: any[]) => R;
type QueryData<Q> = Q extends Query<infer R> ? BaseQueryData<R> : never;
type QueryFor<R> = (api: Client, ...args: any[]) => PostgrestBuilder<R>;

function addType<T>(q: QueryFor<any>) {

}

/* Utility for resource embeding */

function embed<R extends string, Q extends string>(
  relation: R,
  query: Q,
): `${R}(${Q})` {
  return `${relation}(${query})`;
}

/** Principal group query */

export const groupQuery = (api: Client, group_id: string) =>
  api
    .from("groups")
    .select(
      `*,
      job: job_id(*,quote_requests!quote_requests_job_id_fkey(*),quotes!quotes_job_id_fkey(*)),
      organization: organization_id(*,profiles!profiles_organization_id_fkey(*)),
      items!items_group_id_fkey(*,approver: approval_status_updated_by(*), updater: updated_by_user(*)),
      quote_items(*),
      types!types_group_id_fkey(*,approver: approval_status_updated_by(*), updater: updated_by_user(*)),
      documents(*),
      locations(*),
      document_dimensions(*),
      document_notes(*),
      item_reports(*),
      comments!comments_group_id_fkey(*,commenter: user_id(*)),
      attachments!attachments_group_id_fkey(*),
      approver: approval_status_updated_by(*),
      updater: updated_by_user(*)`,
    )
    .eq("id", group_id)
    .order("index", { referencedTable: "documents" })
    .order("created_at", { referencedTable: "items" })
    .order("created_at", { referencedTable: "types" })
    .order("created_at", { referencedTable: "comments" })
    .order("position_pt", { referencedTable: "locations" })
    .order("created_at", { referencedTable: "attachments" })
    .order("sort_order", { referencedTable: "document_notes" })
    .order("created_at", { referencedTable: "document_notes" })
    .order("index", { referencedTable: "item_reports" })
    .maybeSingle();

export type GroupQueryData = QueryData<typeof groupQuery>;
export type GroupItem = GroupQueryData["items"][number];
export type GroupType = GroupQueryData["types"][number];

/** Query for export */

const groupExportQuery = (api: Client, id: string) =>
  api
    .from("items")
    .select("id,width,height,edge_treatments(idx,product)")
    .eq("group_id", id);

export type GroupExportQueryData = QueryData<typeof groupExportQuery>;

/** Query for the full record of a single organization */

export const orgQuerySingle = (api: Client, id: string) =>
  api
    .from("organizations")
    .select(
      `*,
      product_lists!product_lists_vendor_id_fkey(*),
      profiles!profiles_organization_id_fkey(*),
      contacts!contacts_organization_id_fkey(*,contact:contact_id(*),primary_contact:primary_contact_id(*))`,
    )
    .eq("id", id)
    .single();

export type OrgQueryData = QueryData<typeof orgQuerySingle>;

/* Shortcuts for embedded resources of jobs */

const groupOfJob = embed(
  "groups!jobs_current_group_id_fkey",
  `*,
   types(*),
   custom_columns(*),
   custom_collection_columns(*),
   documents(*),
   locations(*),
   document_dimensions(*),
   document_notes(*),
   quote_items(*),
   item_reports(*),
   items(
     *,
     type:types(mark,name))`,
);

const orgOfJob = embed(
  "organizations!jobs_organization_id_fkey",
  "*",
);

/** Job Summary */

export const jobSummaryQuery = (api: Client, jobId: string) =>
  api
    .from("jobs")
    .select(
      `*,
      dimtext_options,
      group: ${groupOfJob},
      organization: ${orgOfJob}`,
    )
    .eq("id", jobId)
    .single();

export type JobSummaryQueryData = QueryData<typeof jobSummaryQuery>;


/** Unpack items by job */

export const jobUnpackedQuery = (api: Client, jobId: string) =>
  api
    .from("jobs")
    .select(`
      id,
      name,
      dimtext_options,
      custom_columns(*),
      group: groups!jobs_current_group_id_fkey(
        items: items_ordered(*,layer:type_makeup_sublayers(*),data )
        )
      `)
    .eq("id", jobId)
    .single();

export type JobUnpackedQueryData = QueryData<typeof jobUnpackedQuery>;

/** Check if a job should be accessible */

export const jobAccessibleQuery = (api: Client, jobId: string) =>
  api
    .from("groups")
    .select("job_id", { count: "exact", head: true })
    .eq("job_id", jobId);
